<template>
	<div class="credit-card-wrapper">
		<full-screen-modal-header
			v-if="!withoutHeader"
			title="Add new card"
			action-text=""
			:action-disabled="isLoading"
			@action="callPayWithCreditCard"
			@close="closeModal"
		/>

		<div class="form-wrapper">
			<h3 v-if="withoutHeader" class="form-wrapper__title">
				Use another card
			</h3>
			<div
				ref="manualCardEntry"
				:id="$options.MANUAL_CARD_ENTRY_ID"
				class="card-entry-wrapper"
				:class="{ 'te-connect-iframe-loaded': isTEconnectIframeLoaded }"
			/>
			<div class="mb-10 hint-text">*All fields are required</div>
			<div class="mb-10 hint-text">
				&#128274; Your payment info will be stored securely
			</div>
			<button class="button" type="button" @click="callPayWithCreditCard">
				{{ buttonText }}
			</button>
		</div>
		<div v-if="!onlyCreditCard" class="pay-btns-wrapper">
			<div
				class="pay-button-wrapper apple-pay-holder"
				:class="{ 'btn-hidden': !isAppleUser }"
			>
				<div class="pay-button-overlay" @click.stop="handleAppleClick" />
				<div :id="$options.BUTTONS_APPLE_ID" class="button-apple-overlay" />
			</div>
			<div
				v-if="!isAppleUser"
				class="google-pay-button pay-button-wrapper"
				ref="google-play-button"
			>
				<div class="pay-button-overlay" @click.stop="handleGoogleClick" />
				<div
					:id="$options.BUTTONS_GOOGLE_ID"
					class="button-google-overlay"
					@click="payWithMagensaGoogle"
				/>
			</div>
		</div>
		<loader-with-title
			v-if="!isTEconnectIframeLoaded || isLoading || parentLoading"
		/>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import {
	PAYMENT_PROVIDER,
	PAYMENT_GATEWAY,
	GOOGLE_PAY_SKIPPED_ERROR
} from '@/constants'
import LoaderWithTitle from '@/components/Loaders/LoaderWithTitle.vue'

import FullScreenModalHeader from './FullScreenModalHeader'
import pickUpValidation from '@/mixins/pickUpValidation'
import routerMixin from '@/mixins/routerMixin'

// Api
import eventApi from '@/api/events'

export default {
	name: 'CreditCardModalMagensa',

	mixins: [pickUpValidation, routerMixin],

	props: {
		amount: {
			type: Number,
			required: true
		},
		isPickUp: {
			type: Boolean,
			default: false
		},
		isTicketing: {
			type: Boolean,
			default: false
		},
		orderId: {
			type: String,
			default: ''
		},
		withoutHeader: {
			type: Boolean,
			default: false
		},
		formBackgroundColor: {
			type: String,
			default: 'transparent'
		},
		parentLoading: {
			type: Boolean,
			default: false
		},
		onlyPaymentMethodSelect: {
			type: Boolean,
			default: false
		},
		onlyCreditCard: {
			type: Boolean,
			default: false
		},
		buttonText: {
			type: String,
			default: 'Pay'
		}
	},

	MANUAL_CARD_ENTRY_ID: 'manual-card-entry-holder',
	PP_MAGENSA: PAYMENT_PROVIDER.MAGENSA,
	BUTTONS_APPLE_ID: 'buttons-apple-pay-node',
	BUTTONS_GOOGLE_ID: 'buttons-google-pay-node',
	TE_CONNECT_IFRAME_ID: '__te-connect-secure-window',

	components: {
		FullScreenModalHeader,
		LoaderWithTitle
	},

	data() {
		return {
			isLoading: false,
			isAppleUser: false,
			isTEconnectIframeLoaded: false
		}
	},

	async mounted() {
		await this.initPaymentInstance()
		await this.initTecPrInterface()
	},

	computed: {
		...mapState('cart', {
			cartId: 'id'
		}),
		...mapState('cart', ['pickupTimeFrom', 'pickupTimeTo']),
		...mapGetters('venue', ['isOrderingTimeAvailable']),
		...mapGetters('payment', ['magensaPaymentInstance', 'tecPrInterface']),

		manualCardEntryStyles() {
			return {
				base: {
					wrapper: {
						margin: 0,
						padding: '5px',
						// direction: 'row',
						direction: 'column'
						// flexWrap: 'wrap-reverse',
					},
					variants: {
						inputType: 'outlined',
						// inputType: 'filled',
						// inputType: 'standard',
						// inputMargin: 'dense',
						inputMargin: 'none',
						// inputMargin: 'normal',
						inputSize: 'medium',
						// inputSize: "small",
						// autoMinHeight: true,
						autoMinHeight: false
					},
					backgroundColor: this.formBackgroundColor
				},
				boxes: {
					// labelColor: '#ffffff',
					labelColor: '#ffffff',
					textColor: '#ffffff',
					// borderRadius: 0,
					borderRadius: 10,
					errorColor: '#7E6DD8',
					inputColor: '#151126'
				}
			}
		}
	},

	methods: {
		...mapActions('venue', [
			'setIsTableSideOrderingAvailable',
			'setPickUpTimeWithoutGap'
		]),

		...mapActions('payment', ['addCreditCard', 'setTecPrInterface']),
		...mapActions('ui', ['addMainError']),

		initGoogleApplePay() {
			this.tecPrInterface.canMakePayments().then(canMakePaymentsResult => {
				if (canMakePaymentsResult) {
					if (canMakePaymentsResult.applePay) {
						this.isAppleUser = true
					}
					const buttonNodes = this.isAppleUser
						? { applePayNode: this.$options.BUTTONS_APPLE_ID }
						: { googlePayNode: this.$options.BUTTONS_GOOGLE_ID }
					const buttonsOptions = this.isAppleUser
						? { applePayOptions: { buttonType: 'plain' } }
						: {
								googlePayOptions: {
									buttonColor: 'white',
									buttonType: 'plain',
									buttonSizeMode: 'fill'
								}
						  }
					this.tecPrInterface.mountTecPrButton(buttonNodes, buttonsOptions)
				}
			})
			this.tecPrInterface.listenFor('confirm-token', tokenResp => {
				const { error, completePayment } = tokenResp
				if (error) {
					if (typeof completePayment === 'function') {
						completePayment('failure')
					}
					if (error !== GOOGLE_PAY_SKIPPED_ERROR) {
						this.addMainError(error)
					}
				} else {
					if (this.isAppleUser === true) {
						completePayment('success')
						this.payWithMagensaApple(tokenResp)
					} else {
						this.payWithMagensaGoogle(tokenResp)
					}
					if (typeof completePayment === 'function') {
						completePayment('success')
					}
				}
			})
		},
		getGooglePaymentRequestObjectOptions() {
			const allowedCardNetworks = [
				'AMEX',
				'DISCOVER',
				'INTERAC',
				'JCB',
				'MASTERCARD',
				'VISA'
			]
			return {
				merchantName: 'SmartTab Corp',
				gatewayId: process.env.VUE_APP_GOOGLE_PAY_GATEWAY_MERCHANT_ID,
				environment: process.env.VUE_APP_GOOGLE_PAY_MAGENSA_ENV, // For Magensa always 'production'
				allowedCardNetworks,
				allowedAuthMethods: ['CRYPTOGRAM_3DS'],
				transactionInfo: {
					totalPriceLabel: 'Total',
					totalPriceStatus: 'ESTIMATED',
					totalPrice: `${this.amount}`,
					currencyCode: 'USD',
					countryCode: 'US'
				}
			}
		},
		getApplePayPaymentRequestObjectOptions() {
			return {
				storeDisplayName: 'SmartTab Corp.',
				currencyCode: 'USD',
				countryCode: 'US',
				supportedNetworks: ['visa', 'masterCard', 'amex', 'discover', 'jcb'],
				merchantCapabilities: ['supports3DS'],
				total: {
					label: 'Total',
					amount: `${this.amount}`,
					type: 'pending'
				}
			}
		},

		initTecPrInterface() {
			const googlePay = this.getGooglePaymentRequestObjectOptions()
			const applePay = this.getApplePayPaymentRequestObjectOptions()
			if (this.tecPrInterface) {
				this.tecPrInterface.updatePaymentRequest({
					googlePay,
					applePay
				})
			} else {
				this.setTecPrInterface(
					this.magensaPaymentInstance.createTecPaymentRequest({
						googlePay,
						applePay
					})
				)
			}
			this.initGoogleApplePay()
		},

		closeModal() {
			this.$emit('close')
		},

		async initPaymentInstance() {
			this.magensaPaymentInstance.mountCardEntry(
				this.$options.MANUAL_CARD_ENTRY_ID,
				this.manualCardEntryStyles
			)
			this.checkIframeLoaded()
		},

		checkIframeLoaded() {
			const iframe = document.getElementById(this.$options.TE_CONNECT_IFRAME_ID)
			if (iframe) {
				iframe.addEventListener('load', () => {
					this.isTEconnectIframeLoaded = true
				})
			} else {
				window.setTimeout(this.checkIframeLoaded, 100)
			}
		},

		async addCardToOrder(creditCard) {
			try {
				const ticketOrder =
					(
						await eventApi.updateEventOrder({
							orderId: this.orderId,
							creditCardId: creditCard.id
						})
					)?.data || {}

				this.$emit('change-ticket-order', ticketOrder)

				if (!this.onlyPaymentMethodSelect) {
					this.$emit('reserve-and-purchase-tickets')
				}

				this.$emit('close')
			} catch (e) {
				if (e?.response?.data?.message === 'Event status is not active') {
					this.$emit('unavailable-event')
				}
			}
		},

		async payWithMagensaGoogle(resp) {
			const { tokenDetails } = resp
			if (tokenDetails) {
				const { token, additionalData } = tokenDetails
				const { info } = additionalData
				const { cardNetwork: cardType, cardDetails: lastFour } = info
				const paymentGatewayType = PAYMENT_GATEWAY.ANDROID_PAY_CARD
				const paymentProvider = PAYMENT_PROVIDER.MAGENSA
				const data = {
					token,
					cardType,
					lastFour,
					paymentGatewayType,
					paymentProvider
				}
				try {
					this.isLoading = true
					const creditCard = await this.addCreditCard(data)

					if (this.isTicketing) {
						await this.addCardToOrder(creditCard)
					}

					this.$emit('credit-card-added', creditCard)

					if (!this.isTicketing) {
						this.$emit('close')
					}

					this.isLoading = false
				} catch (error) {
					this.$emit('show-payment-unavailable-modal')
					this.isLoading = false
				}
			}
		},

		async payWithMagensaApple(resp) {
			const { tokenDetails } = resp
			if (tokenDetails) {
				const { token } = tokenDetails
				const { paymentMethod } = token
				const { network: cardType, displayName } = paymentMethod
				const lastFour = displayName.slice(-4)
				const paymentGatewayType = PAYMENT_GATEWAY.APPLE_PAY_CARD
				const paymentProvider = PAYMENT_PROVIDER.MAGENSA
				const data = {
					token: JSON.stringify(token),
					cardType,
					lastFour,
					paymentGatewayType,
					paymentProvider
				}
				try {
					this.isLoading = true
					const creditCard = await this.addCreditCard(data)

					if (this.isTicketing) {
						await this.addCardToOrder(creditCard)
					}

					this.$emit('credit-card-added', creditCard)

					if (!this.isTicketing) {
						this.$emit('close')
					}

					this.isLoading = false
				} catch (error) {
					this.$emit('show-payment-unavailable-modal')
					this.isLoading = false
				}
			}
		},

		checkIsTimeAvailable() {
			// this.setIsTableSideOrderingAvailable()
			this.setPickUpTimeWithoutGap()
			if (!this.isOrderingTimeAvailable) {
				this.closeModal()
				return this.goToProducts()
			}
		},

		handleGoogleClick() {
			this.checkIsTimeAvailable()
			const button = document
				.querySelector('.credit-card-wrapper')
				.querySelector('.google-pay-button')
				.querySelector('button')
			if (this.isPickUp && !this.isTicketing) {
				const isPickUpValid = this.pickUpValidation(
					this.pickupTimeFrom,
					this.pickupTimeTo
				)
				this.$nextTick(() => {
					if (!this.isTicketing) {
						this.$emit('set-is-pick-up-valid', isPickUpValid)
					}

					if (isPickUpValid && !this.isTicketing) {
						button.click()
					} else {
						this.closeModal()
					}
				})
			} else {
				button.click()
			}
		},

		handleAppleClick() {
			this.checkIsTimeAvailable()
			const button = document
				.querySelector('.credit-card-wrapper')
				.querySelector('.apple-pay-button')
			if (this.isPickUp && !this.isTicketing) {
				const isPickUpValid = this.pickUpValidation(
					this.pickupTimeFrom,
					this.pickupTimeTo
				)
				this.$nextTick(() => {
					if (!this.isTicketing) {
						this.$emit('set-is-pick-up-valid', isPickUpValid)
					}

					if (isPickUpValid && !this.isTicketing) {
						button.click()
					} else {
						this.closeModal()
					}
				})
			} else {
				button.click()
			}
		},

		callPayWithCreditCard() {
			if (this.isPickUp && !this.isTicketing) {
				this.checkIsTimeAvailable()
				const isPickUpValid = this.pickUpValidation(
					this.pickupTimeFrom,
					this.pickupTimeTo
				)
				this.$nextTick(() => {
					this.$emit('set-is-pick-up-valid', isPickUpValid)
					if (isPickUpValid) {
						this.payWithCreditCard()
					} else {
						this.closeModal()
					}
				})
			} else {
				this.payWithCreditCard()
			}
		},

		async payWithCreditCard() {
			this.isLoading = true
			this.magensaPaymentInstance
				.createPayment()
				.then(async payload => {
					if (payload.error) {
						console.log(payload.error)
						this.$emit('payment-unavailable-action')
					} else {
						const data = this.formatMagensaCreditCardResponse(payload)
						const creditCard = await this.addCreditCard(data)

						if (this.isTicketing) {
							await this.addCardToOrder(creditCard)
						}

						this.isLoading = false
						this.$emit('credit-card-added', creditCard)

						if (!this.isTicketing) {
							this.$emit('close')
						}
					}
				})
				.catch(err => {
					console.error(err)
					this.addMainError(err.message)
				})
				.finally(() => (this.isLoading = false))
		},

		formatMagensaCreditCardResponse(payload) {
			const { token, cardMetaData = {} } = payload
			const { maskedPAN = '', expirationDate = '' } = cardMetaData
			const paymentGatewayType = PAYMENT_GATEWAY.CREDIT_CARD
			const bin = maskedPAN.substring(0, 6).replace(/\*/g, '')
			const lastFour = maskedPAN.slice(-4)
			const expirationYear = expirationDate.substring(0, 2)
			const expirationMonth = expirationDate.substring(2, 4)
			const paymentProvider = this.$options.PP_MAGENSA
			return {
				token,
				paymentGatewayType,
				bin,
				expirationYear,
				expirationMonth,
				lastFour,
				paymentProvider
			}
		}
	}
}
</script>

<style scoped lang="scss">
@import './src/assets/styles/variables';

// TODO: add colors variables
.hint-text {
	text-align: center;
	color: #fff;
}
.credit-card-wrapper {
	position: fixed;
	z-index: 13;
	overflow: auto;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: $background-dark;

	/deep/ .header {
		position: static;
		max-width: 430px;
		margin: 0 auto;

		.btn-close {
			&:after {
				display: none;
			}

			&:before {
				position: absolute;
				left: 10px;
				top: 6px;
				width: 12px;
				height: 12px;
				border: solid #fff;
				border-width: 1px 1px 0 0;
				transform: rotate(-135deg);
				content: '';
			}
		}

		.action-button {
			width: 24px;
			visibility: hidden;
			pointer-events: none;
		}
	}

	.button {
		box-sizing: border-box;
		display: block;
		width: calc(100% - 26px);
		margin: 0 13px 24px;
		padding: 16px 5px;
		border-radius: 24px;
		font-size: 18px;
		line-height: 20px;
		letter-spacing: 1.5px;
		background-color: $button-primary-color;
	}

	.apple-pay-button {
		display: block;
		margin: 0 0 24px;
	}
}

label {
	display: block;
	padding-bottom: 5px;
}

.proceed-button {
	display: block;
	width: 100%;
	margin: 0 0 24px;
	padding: 13px 5px;
	box-sizing: border-box;
	text-align: center;
	border-radius: 4px;
}

.card-entry-wrapper {
	height: 400px;
	opacity: 0;
	transition: opacity 0.3s;
	&.te-connect-iframe-loaded {
		opacity: 1;
	}
}

.card-title {
	padding: 0 15px 15px;
	text-align: center;
	color: #ffffff;
}

.action-wrapper {
	padding: 25px;
}

.apple-btn {
	margin-bottom: 25px;
}

.form-wrapper {
	padding-top: 0;
	max-width: 430px;
	margin: 0 auto;

	&__title {
		margin: 0;
		text-align: center;
		color: #fff;
		font-size: 16px;
		line-height: 22px;
		font-weight: 400;
	}
}

.pay-btns-wrapper {
	max-width: 430px;
	margin: 0 auto;
	padding: 0 13px;
}

.btn-hidden {
	height: 0;
	opacity: 0;
	min-height: 0;
}

.pay-button-wrapper {
	position: relative;

	.pay-button-overlay {
		position: absolute;
		z-index: 15;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		cursor: pointer;

		&:has(+ .button-apple-overlay:empty),
		&:has(+ .button-google-overlay:empty) {
			cursor: default;
			pointer-events: none;
		}
	}
}

.apple-pay-holder {
	margin-bottom: 24px;
}
</style>
<style>
#te-connect-apple-pay-btn {
	width: 100%;
	cursor: pointer;
}
.credit-card-wrapper .loader .animated-logo {
	top: 35%;
}
</style>
